'use client'

import * as React from 'react'

import { cn } from '@/lib/utils'
import { Breadcrumbs } from '@/components/breadcrumbs'
import { Button } from '@/components/ui/button'
import { ChromaText } from '@/components/ui/chroma-text'
import { useSolution, type SolutionSlug } from './solution-kit'
import {
  SWEEP_END_EVENT,
  SWEEP_START_EVENT,
} from '@/components/theme-sweep-provider'

type RGB = [number, number, number]
type Palette = { deep: RGB; mid: RGB; tip: RGB; bloom: RGB }

/* Brand-family palettes (mint/teal/sky/indigo) standing in for the original
   Sunset/Aurora/Twilight/Bloom. `deep` dominates the wash near the emitter,
   `tip` colours the thread ends and the tab underline. */
const PALETTES: Palette[] = [
  // 01 · corona — mint to sky
  {
    deep: [55, 236, 176],
    mid: [45, 212, 191],
    tip: [56, 189, 248],
    bloom: [99, 132, 246],
  },
  // 02 · volume — cyan to blue
  {
    deep: [34, 211, 238],
    mid: [56, 189, 248],
    tip: [59, 130, 246],
    bloom: [139, 92, 246],
  },
  // 03 · dome — emerald to cyan
  {
    deep: [16, 185, 129],
    mid: [20, 184, 166],
    tip: [34, 211, 238],
    bloom: [125, 211, 252],
  },
  // 04 · globe — sky to violet
  {
    deep: [56, 189, 248],
    mid: [96, 165, 250],
    tip: [129, 140, 248],
    bloom: [167, 139, 250],
  },
]

type Form = 'corona' | 'volume' | 'dome' | 'globe'
const FORMS: Form[] = ['corona', 'volume', 'dome', 'globe']

const DENSITY = 130
const PUSH_RADIUS = 190
const PUSH_STRENGTH = 130
/* Forms use the band's full height; the solid tab strip masks the canvas top
   so tips vanish beneath it rather than colliding with UI. */
const CORONA_REACH = 1
const VOLUME_REACH = 1
const DOME_REACH = 1
const GLOBE_SCALE = 1
const CLUSTER_Y = 0.48 // morph cluster centre, fraction of H

const GOLDEN = 2.399963229728653
const TWO_PI = Math.PI * 2
const DEG = Math.PI / 180

/* Globe tilt axis and spoke anchor, verbatim from the source. */
const TILT_COS = 0.9427546655283462
const TILT_SIN = 0.3334870921408144
const AXIS_LEN = Math.hypot(-0.5, 0.68, 0.55)
const AXIS = { x: -0.5 / AXIS_LEN, y: 0.68 / AXIS_LEN, z: 0.55 / AXIS_LEN }

const hash = (x: number) => {
  const s = 43758.5453 * Math.sin(127.1 * x + 311.7)
  return s - Math.floor(s)
}
const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x)
const clamp = (x: number, lo: number, hi: number) =>
  x < lo ? lo : x > hi ? hi : x
const lerp = (a: number, b: number, t: number) => a + (b - a) * t
const smoother = (t: number) => {
  const u = clamp01(t)
  return u * u * u * (u * (6 * u - 15) + 10)
}
const mixRGB = (a: RGB, b: RGB, t: number): RGB => [
  lerp(a[0], b[0], t),
  lerp(a[1], b[1], t),
  lerp(a[2], b[2], t),
]
const rgba = (c: RGB, a: number) =>
  `rgba(${Math.round(c[0])},${Math.round(c[1])},${Math.round(c[2])},${a})`

type Line = {
  ox: number
  oy: number
  tx: number
  ty: number
  vtx: number
  vty: number
  sx: number
  sy: number
  sox: number
  soy: number
}

type Target = {
  Ox: number
  Oy: number
  Tx: number
  Ty: number
  A: number
  dx: number
  dy: number
  dz: number
}

type Size = { W: number; H: number }

/* Per-form target for line i of n at time t — a straight port of the
   original C(), with the reach constants folded in. */
function formTarget(
  i: number,
  n: number,
  form: Form,
  t: number,
  size: Size,
  out: Target
) {
  const { W, H } = size
  const cx = W / 2
  const p = hash(1.3 * i + 0.5)
  const u = hash(2.7 * i + 9.1)
  out.A = 1

  if (form === 'corona') {
    const oy = 1.02 * H
    const spread = 200 * DEG
    const angle =
      170 * DEG + ((i + 0.5) / n) * spread + (spread / n) * (u - 0.5) * 1.6
    const len = H * (0.26 + 0.5 * p) * CORONA_REACH
    const pulse = 1 + 0.045 * Math.sin(0.7 * t + 0.6 * i)
    out.Ox = cx
    out.Oy = oy
    out.Tx = cx + Math.cos(angle) * len * pulse
    out.Ty = oy + Math.sin(angle) * len * pulse
    return
  }

  if (form === 'volume') {
    const x = lerp(0.06 * W, 0.94 * W, n === 1 ? 0.5 : i / (n - 1))
    const o = i / Math.max(1, n - 1)
    const h =
      H *
      (0.1 +
        0.62 *
          clamp01(
            0.5 +
              0.32 * Math.sin(o * TWO_PI * 2 - 0.55 * t) +
              0.2 * Math.sin(o * TWO_PI * 3.3 + 0.36 * t)
          )) *
      VOLUME_REACH
    const sway = Math.sin(0.38 * t + 8 * o) * W * 0.008
    out.Ox = x
    out.Oy = H
    out.Tx = x + sway
    out.Ty = H - h
    return
  }

  if (form === 'globe') {
    const R = Math.min(0.5 * W, 0.62 * H) * GLOBE_SCALE
    const cy = 0.92 * H
    const e = 1 - (2 * (i + 0.5)) / n
    const b = Math.sqrt(Math.max(0, 1 - e * e))
    const phi = GOLDEN * i
    const px = Math.cos(phi) * b
    const pz = Math.sin(phi) * b
    const spin = 0.035 * t
    const cs = Math.cos(spin)
    const sn = Math.sin(spin)
    const rz = -px * sn + pz * cs
    const vx = px * cs + pz * sn
    const vy = TILT_COS * e - TILT_SIN * rz
    const vz = TILT_SIN * e + TILT_COS * rz
    out.dx = vx
    out.dy = vy
    out.dz = vz
    out.Ox = out.Tx = cx + vx * R
    out.Oy = out.Ty = cy - vy * R
    out.A = clamp01((vz + 0.15) / 0.85)
    return
  }

  // dome
  const rise = 0.56 * H
  const angle = ((i + 0.5) / n) * Math.PI + (Math.PI / n) * (u - 0.5) * 1.3
  const len = (0.58 + 0.42 * p) * DOME_REACH
  const wobble = 1 + 0.02 * Math.sin(0.6 * t + 0.5 * i)
  out.Ox = cx
  out.Oy = H
  out.Tx = cx + 0.42 * W * Math.cos(angle) * len * wobble
  out.Ty = H - Math.sin(angle) * rise * len * wobble
}

/* ─── Component ────────────────────────────────────────────────────────── */

export function FilamentHero({ slug }: { slug: SolutionSlug }) {
  const solution = useSolution(slug)
  const steps = solution.howItWorks.slice(0, 4)

  const wrapRef = React.useRef<HTMLDivElement>(null)
  const canvasRef = React.useRef<HTMLCanvasElement>(null)
  const [active, setActive] = React.useState(0)
  const [faded, setFaded] = React.useState(false)

  const activeRef = React.useRef(0)
  const morphQueuedRef = React.useRef(false)
  const paletteTargetRef = React.useRef<Palette>(PALETTES[0]!)
  const staticRedrawRef = React.useRef<(() => void) | null>(null)

  const selectTab = (idx: number) => {
    if (idx === activeRef.current) return
    morphQueuedRef.current = true
    activeRef.current = idx
    paletteTargetRef.current = PALETTES[idx]!
    setActive(idx)
    staticRedrawRef.current?.()
  }

  React.useEffect(() => {
    const wrap = wrapRef.current
    const canvas = canvasRef.current
    if (!wrap || !canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const reduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches

    const size: Size = { W: wrap.clientWidth, H: wrap.clientHeight }
    let dark = document.documentElement.classList.contains('dark')
    let baseFill = getComputedStyle(wrap).backgroundColor

    const measure = () => {
      size.W = wrap.clientWidth
      size.H = wrap.clientHeight
      const dpr = Math.min(2, window.devicePixelRatio || 1)
      canvas.width = Math.round(size.W * dpr)
      canvas.height = Math.round(size.H * dpr)
      canvas.style.width = `${size.W}px`
      canvas.style.height = `${size.H}px`
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    measure()

    const lineCount = Math.round(DENSITY * (size.W < 768 ? 0.55 : 1))
    const lines: Line[] = []
    for (let i = 0; i < lineCount; i++) {
      lines.push({
        ox: size.W / 2,
        oy: size.H / 2,
        tx: size.W / 2,
        ty: size.H / 2,
        vtx: 0,
        vty: 0,
        sx: size.W / 2,
        sy: size.H / 2,
        sox: size.W / 2,
        soy: size.H / 2,
      })
    }

    /* In-flight palette eases toward the target at 6%/frame. */
    const paletteNow: Palette = {
      deep: [...paletteTargetRef.current.deep] as RGB,
      mid: [...paletteTargetRef.current.mid] as RGB,
      tip: [...paletteTargetRef.current.tip] as RGB,
      bloom: [...paletteTargetRef.current.bloom] as RGB,
    }

    const pointer = { x: -9999, y: -9999, inside: false }
    const onPointerMove = (e: PointerEvent) => {
      if (
        e.target instanceof Element &&
        e.target.closest('[data-filament-ui]')
      ) {
        pointer.inside = false
        pointer.x = -9999
        pointer.y = -9999
        return
      }
      const r = wrap.getBoundingClientRect()
      pointer.x = e.clientX - r.left
      pointer.y = e.clientY - r.top
      pointer.inside = true
    }
    const onPointerLeave = () => {
      pointer.inside = false
      pointer.x = -9999
      pointer.y = -9999
    }
    wrap.addEventListener('pointermove', onPointerMove)
    wrap.addEventListener('pointerleave', onPointerLeave)

    let morphStart = -999
    const target: Target = {
      Ox: 0,
      Oy: 0,
      Tx: 0,
      Ty: 0,
      A: 1,
      dx: 0,
      dy: 0,
      dz: 0,
    }

    const draw = (t: number, animated: boolean) => {
      const form = FORMS[activeRef.current]!
      const n = lines.length

      if (morphQueuedRef.current) {
        morphQueuedRef.current = false
        morphStart = t
        for (const l of lines) {
          l.sx = l.tx
          l.sy = l.ty
          l.sox = l.ox
          l.soy = l.oy
          l.vtx = 0
          l.vty = 0
        }
      }

      const ease = animated ? 0.06 : 1
      const pt = paletteTargetRef.current
      paletteNow.deep = mixRGB(paletteNow.deep, pt.deep, ease)
      paletteNow.mid = mixRGB(paletteNow.mid, pt.mid, ease)
      paletteNow.tip = mixRGB(paletteNow.tip, pt.tip, ease)
      paletteNow.bloom = mixRGB(paletteNow.bloom, pt.bloom, ease)

      ctx.globalCompositeOperation = 'source-over'
      ctx.fillStyle = baseFill
      ctx.fillRect(0, 0, size.W, size.H)

      const grad = ctx.createRadialGradient(
        size.W / 2,
        size.H,
        0,
        size.W / 2,
        size.H,
        size.H
      )
      grad.addColorStop(0, rgba(paletteNow.deep, dark ? 0.88 : 0.9))
      grad.addColorStop(0.18, rgba(paletteNow.mid, dark ? 0.74 : 0.76))
      grad.addColorStop(0.36, rgba(paletteNow.tip, dark ? 0.52 : 0.54))
      grad.addColorStop(0.52, rgba(paletteNow.bloom, dark ? 0.34 : 0.36))
      grad.addColorStop(0.66, rgba(paletteNow.bloom, dark ? 0.19 : 0.2))
      grad.addColorStop(0.8, rgba(paletteNow.bloom, dark ? 0.07 : 0.08))
      grad.addColorStop(0.92, rgba(paletteNow.bloom, 0.02))
      grad.addColorStop(1, rgba(paletteNow.bloom, 0))
      ctx.fillStyle = grad
      ctx.fillRect(0, 0, size.W, size.H)

      /* Dark theme draws additively — the glow is the composite mode, not
         shadowBlur. */
      ctx.globalCompositeOperation = dark ? 'lighter' : 'source-over'

      const master = dark ? 0.5 : 0.72
      const dotAlpha = dark ? 0.9 : 0.95
      const thread = dark
        ? mixRGB(paletteNow.tip, [250, 250, 250], 0.4)
        : mixRGB(paletteNow.deep, [9, 9, 11], 0.42)

      const since = t - morphStart
      const inMorph = since >= 0 && since < 2.1
      const inCollapse = inMorph && since < 0.72
      const collapseP = smoother(since / 0.72)
      const expandP = smoother((since - 0.72) / 1.38)
      const clusterX = size.W / 2
      const clusterY = CLUSTER_Y * size.H
      const clusterR = 0.06 * Math.min(size.W, size.H)
      const isGlobe = form === 'globe'
      const globeR = Math.min(0.5 * size.W, 0.62 * size.H) * GLOBE_SCALE
      const globeCx = size.W / 2
      const globeCy = 0.92 * size.H

      for (let i = 0; i < n; i++) {
        const line = lines[i]!
        formTarget(i, n, form, t, size, target)

        let tx = target.Tx
        let ty = target.Ty
        if (pointer.inside) {
          if (form === 'volume') {
            const dx = target.Ox - pointer.x
            if (Math.abs(dx) < PUSH_RADIUS) {
              const f = (1 - Math.abs(dx) / PUSH_RADIUS) ** 2
              ty -= f * PUSH_STRENGTH * 1.1
              tx +=
                clamp(pointer.x - target.Ox, -1, 1) * f * PUSH_STRENGTH * 0.12
            }
          } else {
            const dx = tx - pointer.x
            const dy = ty - pointer.y
            const dist = Math.hypot(dx, dy) || 1
            if (dist < PUSH_RADIUS) {
              const f = (1 - dist / PUSH_RADIUS) ** 2 * PUSH_STRENGTH
              tx += (dx / dist) * f
              ty += (dy / dist) * f
            }
          }
        }

        if (inMorph) {
          const e = 1 - (2 * (i + 0.5)) / n
          const cx =
            clusterX +
            Math.cos(GOLDEN * i) * Math.sqrt(Math.max(0, 1 - e * e)) * clusterR
          const cy = clusterY - e * clusterR
          if (inCollapse) {
            line.tx = lerp(line.sx, cx, collapseP)
            line.ty = lerp(line.sy, cy, collapseP)
            line.ox = lerp(line.sox, cx, collapseP)
            line.oy = lerp(line.soy, cy, collapseP)
          } else {
            line.tx = lerp(cx, tx, expandP)
            line.ty = lerp(cy, ty, expandP)
            line.ox = lerp(cx, target.Ox, expandP)
            line.oy = lerp(cy, target.Oy, expandP)
          }
          line.vtx = 0
          line.vty = 0
        } else {
          line.ox += (target.Ox - line.ox) * 0.1
          line.oy += (target.Oy - line.oy) * 0.1
          line.vtx += (tx - line.tx) * 0.11
          line.vty += (ty - line.ty) * 0.11
          line.vtx *= 0.72
          line.vty *= 0.72
          line.tx += line.vtx
          line.ty += line.vty
        }

        const depth = target.A
        if (isGlobe) {
          const spokeA = expandP * depth
          if (spokeA > 0.02) {
            const angle = Math.acos(
              clamp(
                AXIS.x * target.dx + AXIS.y * target.dy + AXIS.z * target.dz,
                -1,
                1
              )
            )
            if (angle > 0.02) {
              const sinA = Math.sin(angle)
              ctx.strokeStyle = rgba(thread, 0.8 * master * spokeA)
              ctx.lineWidth = 0.7
              ctx.beginPath()
              for (let s = 0; s <= 22; s++) {
                const f = s / 22
                const wa = Math.sin((1 - f) * angle) / sinA
                const wb = Math.sin(f * angle) / sinA
                const px = globeCx + globeR * (AXIS.x * wa + target.dx * wb)
                const py = globeCy - globeR * (AXIS.y * wa + target.dy * wb)
                if (s === 0) ctx.moveTo(px, py)
                else ctx.lineTo(px, py)
              }
              ctx.stroke()
            }
          }
          ctx.globalCompositeOperation = 'source-over'
          ctx.fillStyle = rgba(thread, dotAlpha * depth)
          ctx.beginPath()
          ctx.arc(line.tx, line.ty, 1.5, 0, TWO_PI)
          ctx.fill()
          ctx.globalCompositeOperation = dark ? 'lighter' : 'source-over'
        } else {
          const stroke = ctx.createLinearGradient(
            line.ox,
            line.oy,
            line.tx,
            line.ty
          )
          stroke.addColorStop(0, rgba(thread, 0.62 * master * depth))
          stroke.addColorStop(0.5, rgba(thread, 0.88 * master * depth))
          stroke.addColorStop(1, rgba(thread, master * depth))
          ctx.strokeStyle = stroke
          ctx.lineWidth = 0.9
          ctx.beginPath()
          ctx.moveTo(line.ox, line.oy)
          if (form === 'dome') {
            const ax = size.W / 2
            const ay = 0.38 * size.H
            const mx = (line.ox + line.tx) / 2
            const my = (line.oy + line.ty) / 2
            ctx.quadraticCurveTo(
              mx + (ax - mx) * 0.3,
              my + (ay - my) * 0.3,
              line.tx,
              line.ty
            )
          } else {
            ctx.lineTo(line.tx, line.ty)
          }
          ctx.stroke()
          ctx.globalCompositeOperation = 'source-over'
          ctx.fillStyle = rgba(thread, dotAlpha * depth)
          ctx.beginPath()
          ctx.arc(line.tx, line.ty, 1.7, 0, TWO_PI)
          ctx.fill()
          ctx.globalCompositeOperation = dark ? 'lighter' : 'source-over'
        }
      }
      ctx.globalCompositeOperation = 'source-over'
    }

    const placeAtTargets = () => {
      const form = FORMS[activeRef.current]!
      for (let i = 0; i < lines.length; i++) {
        formTarget(i, lines.length, form, 0, size, target)
        const line = lines[i]!
        line.ox = target.Ox
        line.oy = target.Oy
        line.tx = target.Tx
        line.ty = target.Ty
        line.vtx = 0
        line.vty = 0
      }
    }

    const refreshTheme = () => {
      dark = document.documentElement.classList.contains('dark')
      baseFill = getComputedStyle(wrap).backgroundColor
    }

    if (reduced) {
      /* One faithful frame, re-painted on tab/theme/size changes only. */
      const drawStatic = () => {
        morphQueuedRef.current = false
        morphStart = -999
        placeAtTargets()
        draw(0, false)
      }
      drawStatic()
      setFaded(true)
      staticRedrawRef.current = drawStatic

      const mo = new MutationObserver(() => {
        refreshTheme()
        drawStatic()
      })
      mo.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      })
      const ro = new ResizeObserver(() => {
        measure()
        drawStatic()
      })
      ro.observe(wrap)
      return () => {
        mo.disconnect()
        ro.disconnect()
        wrap.removeEventListener('pointermove', onPointerMove)
        wrap.removeEventListener('pointerleave', onPointerLeave)
        staticRedrawRef.current = null
      }
    }

    /* Animated path: seed every line inside the cluster and backdate the
       morph clock so the first frames are the 1.38 s expansion. */
    for (let i = 0; i < lines.length; i++) {
      const e = 1 - (2 * (i + 0.5)) / lines.length
      const b = Math.sqrt(Math.max(0, 1 - e * e))
      const line = lines[i]!
      line.ox = line.tx =
        size.W / 2 +
        Math.cos(GOLDEN * i) * b * (0.06 * Math.min(size.W, size.H))
      line.oy = line.ty =
        CLUSTER_Y * size.H - e * (0.06 * Math.min(size.W, size.H))
    }
    morphStart = -0.72
    setFaded(true)

    let running = false
    let rafId = 0
    let firstTs = 0
    let lastT = 0
    const frame = (ts: number) => {
      if (!running) return
      if (!firstTs) firstTs = ts
      lastT = (ts - firstTs) / 1000
      draw(lastT, true)
      rafId = requestAnimationFrame(frame)
    }
    const start = () => {
      if (running) return
      running = true
      rafId = requestAnimationFrame(frame)
    }
    const stop = () => {
      running = false
      if (rafId) cancelAnimationFrame(rafId)
      rafId = 0
    }

    let onScreen = true
    let sweeping = false
    const sync = () => {
      if (onScreen && !sweeping && !document.hidden) start()
      else stop()
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        onScreen = !!entry?.isIntersecting
        sync()
      },
      { threshold: 0 }
    )
    io.observe(wrap)
    const onVisibility = () => sync()
    document.addEventListener('visibilitychange', onVisibility)

    /* Stand down while the theme curtain is sweeping: this field is hidden
       beneath it, and competing for the main thread makes the curtain judder. */
    const onSweepStart = () => {
      sweeping = true
      sync()
    }
    const onSweepEnd = () => {
      sweeping = false
      sync()
    }
    window.addEventListener(SWEEP_START_EVENT, onSweepStart)
    window.addEventListener(SWEEP_END_EVENT, onSweepEnd)

    /* Resizing reallocates the backing store, which clears it. Repaint in the
       same task so a blank canvas is never shown — the loop may be stopped
       (off-screen, hidden tab) and would otherwise leave it empty. */
    let resizeTimer = 0
    const ro = new ResizeObserver(() => {
      window.clearTimeout(resizeTimer)
      resizeTimer = window.setTimeout(() => {
        measure()
        draw(lastT, true)
      }, 150)
    })
    ro.observe(wrap)

    const mo = new MutationObserver(refreshTheme)
    mo.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    })

    return () => {
      stop()
      io.disconnect()
      ro.disconnect()
      mo.disconnect()
      window.clearTimeout(resizeTimer)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener(SWEEP_START_EVENT, onSweepStart)
      window.removeEventListener(SWEEP_END_EVENT, onSweepEnd)
      wrap.removeEventListener('pointermove', onPointerMove)
      wrap.removeEventListener('pointerleave', onPointerLeave)
    }
  }, [])

  const tip = PALETTES[active]!.tip

  return (
    <section
      className={cn(
        'relative flex min-h-[100svh] w-full flex-col overflow-hidden bg-background',
        'mt-[calc(var(--header-h)*-1)]'
      )}
    >
      {/* Title block — left-aligned like every other solution hero. */}
      <div className="container mx-auto pt-[calc(var(--header-h)+clamp(2.5rem,7vh,4.5rem))]">
        <Breadcrumbs
          className="mb-5"
          items={[
            { name: 'Home', href: '/' },
            { name: 'Solutions', href: '/solutions' },
            { name: solution.title, href: `/solutions/${slug}` },
          ]}
        />

        <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
          {solution.badge}
        </p>

        <h1
          className={cn(
            'font-heading mt-5 max-w-4xl font-normal tracking-tight text-foreground',
            'text-[34px]/[1.12] sm:text-5xl/[1.12] md:text-6xl/[1.1] lg:text-[64px]/[1.08]'
          )}
        >
          <ChromaText>{solution.heading ?? solution.title}</ChromaText>
        </h1>

        <p className="font-heading mt-4 max-w-3xl text-xl font-normal leading-snug text-foreground/80 sm:text-2xl md:text-3xl">
          {solution.subtitle}
        </p>

        <p className="mt-5 max-w-2xl text-lg font-medium leading-snug text-emerald-600 sm:text-xl dark:text-primary-active">
          {solution.tagline}
        </p>

        <div className="mt-9 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
          <Button asChild size="xl">
            <a href="/schedule-meeting">Talk to us</a>
          </Button>
          <Button variant="outline" asChild size="xl">
            <a href="#delivers">
              {solution.deliversAccent === 'Delivers'
                ? 'What it delivers'
                : 'What it enables'}
            </a>
          </Button>
        </div>
      </div>

      {/* Filament band — a hairline-boxed panel, tabs on a solid strip, field
          below. The canvas fills the box; the strip masks its top so threads
          never cross the UI. */}
      <div className="mt-[clamp(2.5rem,6vh,4rem)] flex min-h-[max(620px,78svh)] w-full flex-1 pb-[clamp(1.25rem,3vh,2rem)] md:min-h-[max(680px,90svh)]">
        <div
          ref={wrapRef}
          className="relative mx-auto w-[calc(100%-2rem)] max-w-[1336px] overflow-hidden border border-border/70 bg-background sm:w-[calc(100%-4rem)]"
        >
          <canvas
            ref={canvasRef}
            aria-hidden
            className="absolute inset-0 block transition-opacity duration-1000 ease-out"
            style={{ opacity: faded ? 1 : 0 }}
          />

          <div className="pointer-events-none absolute inset-x-0 top-0 z-20 bg-background">
            <div
              role="group"
              aria-label="AI infrastructure stages"
              className="mx-auto flex w-full items-start"
              style={{
                maxWidth: 'min(1180px, 92%)',
                gap: 'clamp(0.5rem, 2vw, 2rem)',
                paddingTop: 'clamp(1rem, 2.5vh, 1.75rem)',
              }}
            >
              {steps.map((step, i) => {
                const selected = i === active
                return (
                  <button
                    key={step.step}
                    type="button"
                    data-filament-ui
                    aria-pressed={selected}
                    onClick={() => selectTab(i)}
                    className="pointer-events-auto flex-1 cursor-pointer text-center transition-opacity"
                    style={{ opacity: selected ? 1 : 0.62, minWidth: 0 }}
                  >
                    <div
                      className="font-heading"
                      style={{
                        fontSize: 'clamp(1.6rem, 4vw, 3rem)',
                        lineHeight: 1,
                        letterSpacing: '-0.02em',
                        color: selected
                          ? 'hsl(var(--foreground) / 0.95)'
                          : 'hsl(var(--foreground) / 0.42)',
                        transition: 'color 0.4s',
                      }}
                    >
                      {step.step}
                    </div>
                    <div
                      className="mx-auto"
                      style={{
                        marginTop: 'clamp(0.35rem, 1vh, 0.6rem)',
                        fontSize: 'clamp(0.66rem, 1.15vw, 0.86rem)',
                        lineHeight: 1.25,
                        maxWidth: '18ch',
                        color: selected
                          ? 'hsl(var(--foreground) / 0.7)'
                          : 'hsl(var(--foreground) / 0.42)',
                        transition: 'color 0.4s',
                      }}
                    >
                      {step.title}
                    </div>
                  </button>
                )
              })}
            </div>

            <div style={{ marginTop: 'clamp(0.9rem, 2.4vh, 1.4rem)' }}>
              {/* Rule runs edge to edge so it meets the box borders; the
                  coloured indicator stays aligned under its tab. */}
              <div className="relative h-px w-full bg-border/70">
                <div
                  className="absolute left-1/2 flex w-full -translate-x-1/2"
                  style={{
                    top: -1,
                    gap: 'clamp(0.5rem, 2vw, 2rem)',
                    maxWidth: 'min(1180px, 92%)',
                  }}
                >
                  {steps.map((step, i) => (
                    <div
                      key={step.step}
                      className="flex-1"
                      style={{ minWidth: 0 }}
                    >
                      <span
                        style={{
                          display: 'block',
                          width: '100%',
                          height: 2,
                          borderRadius: 2,
                          background: rgba(PALETTES[i]!.tip, 1),
                          boxShadow:
                            i === active
                              ? `0 0 12px ${rgba(tip, 0.6)}`
                              : 'none',
                          transformOrigin: 'left center',
                          transform: i === active ? 'scaleX(1)' : 'scaleX(0)',
                          transition:
                            'transform 0.55s cubic-bezier(0.4,0,0.1,1), background 0.4s',
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
